<script lang="tsx" setup>
import { onMounted, ref } from "vue"
import { useRoute } from "vue-router"

import { useStudentPage } from "@/composables/use-student-page"
import { setPage } from "@/composables/set-page"
import { useSiblings } from "@/composables/use-siblings"
import { KirbyStudentPreviewData } from "@/queries/student"

import Main from "@/components/Wrappers/Main.vue"
import PageSection from "@/components/Layout/PageSection.vue"
import InfoPanel from "@/components/Layout/InfoPanel.vue"
import TitleBlock from "@/components/Layout/TitleBlock.vue"
import ResponsiveBodyText from "@/components/Text/ResponsiveBodyText.vue"
import TextLabel from "@/components/Text/TextLabel.vue"
import StudentHeader from "@/components/StudentPage/StudentHeader.vue"
import StudentGalleryWrapper from "@/components/StudentPage/StudentGalleryWrapper.vue"
import StudentProgramme from "@/components/StudentPage/StudentProgramme.vue"
import StudentContact from "@/components/StudentPage/StudentContact.vue"
import StudentArchive from "@/components/StudentPage/StudentArchive.vue"
import StudentRelated from "@/components/StudentPage/StudentRelated.vue"
import ProjectGrid from "@/components/Projects/ProjectGrid.vue"
import PageSpacer from "@/components/Layout/PageSpacer.vue"

const route = useRoute()
const { page, blocks, studentName } = await useStudentPage(route)

const siblings = ref<KirbyStudentPreviewData[] | null>(null)
setPage(page)

onMounted(async () => {
  const { getSiblings } = await useSiblings(page)
  siblings.value = getSiblings(page.id, 99)
})
</script>

<template>
  <Main>
    <div>
      <!-- <div
        v-if="blocks.length"
        v-motion-fade
        class="absolute top-0 h-0 w-full left-0"
      >
        <StudentHeader :name="studentName || page.name" :title="page.title" />
      </div> -->

      <StudentGalleryWrapper
        v-if="blocks.length"
        :blocks="blocks"
        :offset-header="true"
      />
      <PageSpacer v-else class="border-b">
        <div class="text-sm">No media found for this project...</div>
      </PageSpacer>
    </div>

    <PageSection>
      <InfoPanel :fade-header="blocks.length > 0">
        <template #header>
          <TextLabel>Title:</TextLabel>
          <TitleBlock :subtitle="page.title" />
        </template>

        <template #meta>
          <div class="gap-8 lg:gap-6 flex flex-col body-text">
            <div v-if="studentName || page.name">
              <TextLabel>Name:</TextLabel>
              <TitleBlock :name="studentName || page.name" />
            </div>
            <div>
              <StudentProgramme
                v-if="page.programme"
                :programme="page.programme"
                :year="page.year"
              />
            </div>
            <div>
              <StudentContact
                v-if="page.email || page.website || page.instagram"
                :email="page.email"
                :website="page.website"
                :instagram="page.instagram"
              />
            </div>
            <div>
              <StudentArchive
                v-if="page.archive?.length"
                :links="page.archive"
              />
            </div>
          </div>
        </template>

        <template #body>
          <div>
            <TextLabel v-if="page.text">Project text:</TextLabel>
            <ResponsiveBodyText :html="page.text" />
          </div>
        </template>
      </InfoPanel>
    </PageSection>

    <PageSection border v-if="siblings === null || siblings.length">
      <ProjectGrid :students="siblings" v-if="siblings?.length">
        Other projects from
        <RouterLink class="underline link" :to="'/' + page.programme.uri">{{
          page.programme.title
        }}</RouterLink
        >:
      </ProjectGrid>
      <div v-else class="body-text opacity-50">Loading related projects...</div>
    </PageSection>
  </Main>
</template>
